import { CouponIcon, EditNoteIcon } from "@xola/icons";
import { Tooltip } from "@xola/ui-kit";
import clsx from "clsx";
import React, { ReactNode } from "react";
import { Purchase } from "types";
import { useVisibility } from "../../hooks/useVisibility";
import { useCreatePurchaseStore } from "../../stores/createPurchaseStore";
import { ProductImage } from "../Common/ProductImage";
import { SecondaryButton } from "../Common/SecondaryButton";
import { EditPurchaseCodesModal } from "../PurchaseModification/EditPurchaseCodesModal";
import { EditPurchaseTaxesAndFeesModal } from "../PurchaseModification/EditPurchaseTaxesAndFeesModal";
import { usePackage } from "@/hooks/usePackage";
import { getTaxFeeLineItems } from "@/models/PurchaseItem";
import { canViewPartnerPackages, showPackages } from "@/stores/globalStore";

interface PackageSummaryProps {
    purchase: Purchase;
    isActive?: boolean;
    children: ReactNode;
}

export const PackageSummary = ({ purchase, isActive = false, children }: PackageSummaryProps) => {
    const packageId = useCreatePurchaseStore((state) => state.getPurchasePackageId());
    const activeItemIndex = useCreatePurchaseStore((state) => state.activeItemIndex);
    const [isModifyCodesOpen, { show: showModifyCodes, hide: hideModifyCodes }] = useVisibility(false);
    const [isModifyTaxesOpen, { show: showModifyTaxes, hide: hideModifyTaxes }] = useVisibility(false);
    const itemWithTax = getItemWithTaxFeeLineItems(purchase);
    const enabledForRegular = showPackages();
    const enabledForPartner = canViewPartnerPackages();

    const { pkg } = usePackage(packageId, { enabledForRegular, enabledForPartner });

    return pkg ? (
        <>
            <div
                className={clsx(
                    "space-y-3 rounded py-5 px-6",
                    isActive ? "border border-primary bg-primary-lighter" : "bg-white",
                )}
                data-testid="package-summary-product"
            >
                <div className="flex justify-between space-x-1">
                    <div className="flex">
                        <ProductImage product={pkg} size="small" className="mr-1" />
                        <div className="w-full">
                            <div className="text-md font-bold">{pkg.name}</div>
                        </div>
                    </div>
                </div>
                {children}

                <div className="space-x-1">
                    {itemWithTax && (
                        <Tooltip content="Modify Taxes & Fees">
                            <SecondaryButton size="small" onClick={showModifyTaxes}>
                                <EditNoteIcon />
                            </SecondaryButton>
                        </Tooltip>
                    )}

                    <Tooltip content="Modify Codes">
                        <SecondaryButton size="small" onClick={showModifyCodes}>
                            <CouponIcon />
                        </SecondaryButton>
                    </Tooltip>
                </div>
            </div>

            <EditPurchaseCodesModal
                purchase={purchase}
                item={purchase.items[activeItemIndex]}
                isOpen={isModifyCodesOpen}
                onClose={hideModifyCodes}
            />

            {itemWithTax && (
                <EditPurchaseTaxesAndFeesModal
                    purchase={purchase}
                    item={itemWithTax}
                    isOpen={isModifyTaxesOpen}
                    onClose={hideModifyTaxes}
                />
            )}
        </>
    ) : null;
};

const getItemWithTaxFeeLineItems = (purchase: Purchase) => {
    for (const item of purchase.items) {
        const taxFeesLineItems = getTaxFeeLineItems(item);
        if (taxFeesLineItems?.length > 0) {
            return item;
        }
    }
};
